import { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationOnIcon,
  ConfirmationNumber as TicketIcon,
  AccessTime as ClockIcon,
} from '../../components/ui';
import { UserRole, UserStatus } from '../../types/ticket';
import type { User } from '../../types/ticket';
import './UserCard.css';

interface UserCardProps {
  user: User;
  ticketsCount?: number;
  location?: string;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  isCurrentUser?: boolean;
}

export const UserCard: React.FC<UserCardProps> = ({
  user,
  ticketsCount = 0,
  location = 'Not Assigned',
  onEdit,
  onDelete,
  isCurrentUser = false,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEdit = () => {
    onEdit(user);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(user);
    handleMenuClose();
  };

  const getRoleBadgeClass = (): string => {
    if (user.role === UserRole.ADMIN) return 'user-card__role-badge--admin';
    if (user.role === UserRole.T2_TECH) return 'user-card__role-badge--t2';
    return 'user-card__role-badge--t1';
  };

  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="user-card">
      {/* Header with Avatar, Name, Email and Menu */}
      <div className="user-card__header">
        <div className="user-card__header-content">
          <div
            className="user-card__avatar"
            style={{
              background: user.role === UserRole.ADMIN
                ? 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)'
                : user.role === UserRole.T2_TECH
                ? 'linear-gradient(135deg, #0078d4 0%, #106ebe 100%)'
                : 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
            }}
          >
            {getInitials(user.name)}
          </div>

          <div className="user-card__header-info">
            <h3 className="user-card__name">{user.name}</h3>
            <p className="user-card__email" title={user.email}>{user.email}</p>
          </div>
        </div>

        <IconButton
          size="small"
          onClick={handleMenuOpen}
          disabled={isCurrentUser}
          className="user-card__menu-button"
        >
          <MoreVertIcon />
        </IconButton>
      </div>

      <div className="user-card__divider" />

      {/* Role & Status Row */}
      <div className="user-card__role-section">
        <div className="user-card__role-group">
          <span className="user-card__section-label">Role</span>
          <span className={`user-card__role-badge ${getRoleBadgeClass()}`}>
            {user.role}
          </span>
        </div>
        <div className="user-card__status-group">
          <span className="user-card__section-label">Status</span>
          <div className="user-card__status-badge">
            <span className={`user-card__status-icon ${
              user.status === UserStatus.ACTIVE
                ? 'user-card__status-icon--active'
                : 'user-card__status-icon--inactive'
            }`} />
            <span className="user-card__status-text">{user.status}</span>
          </div>
        </div>
      </div>

      <div className="user-card__divider" />

      {/* Activity Metrics - Horizontal Row */}
      <div className="user-card__metrics">
        <div className="user-card__metric">
          <div className="user-card__metric-icon user-card__metric-icon--location">
            <LocationOnIcon sx={{ fontSize: 16 }} />
          </div>
          <div className="user-card__metric-content">
            <span className="user-card__metric-label">Location</span>
            <span className="user-card__metric-value">{location}</span>
          </div>
        </div>

        <div className="user-card__metric-separator" />

        <div className="user-card__metric">
          <div className="user-card__metric-icon user-card__metric-icon--tickets">
            <TicketIcon sx={{ fontSize: 16 }} />
          </div>
          <div className="user-card__metric-content">
            <span className="user-card__metric-label">Active Tickets</span>
            <span className="user-card__metric-value">{ticketsCount}</span>
          </div>
        </div>

        <div className="user-card__metric-separator" />

        <div className="user-card__metric">
          <div className="user-card__metric-icon user-card__metric-icon--time">
            <ClockIcon sx={{ fontSize: 16 }} />
          </div>
          <div className="user-card__metric-content">
            <span className="user-card__metric-label">Last Login</span>
            <span className="user-card__metric-value">
              {user.lastLogin ? formatRelativeTime(user.lastLogin) : 'Never'}
            </span>
          </div>
        </div>
      </div>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1.5 }} />
          Edit User
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
          Delete User
        </MenuItem>
      </Menu>
    </div>
  );
};
